import { getAPIToken } from '@/services/iframe';
import { iframeRequestSystemEnabled } from '@/utils/iframeUtility';

const TOKEN_KEY = 'botcast_mobile_token';

/** Nonce data stored per SSO provider, used to validate the SSO callback. */
interface SSONonce {
  /** Random nonce string, or `null` when none is stored for the provider */
  nonce: string | null;
  /** Extra values that were appended to the nonce with `::` separators (e.g. redirect info) */
  extraData: string[];
}

/**
 * Manage the how Access Tokens are being stored and retreived from storage.
 *
 * Current implementation stores to localStorage. Local Storage should always be
 * accessed through this instace.
**/
const TokenService = {
    /**
     * Obtain prerequisites to set up the token service
     */
    async init(): Promise<void> {
        if (!TokenService.getToken()) {
            if (!iframeRequestSystemEnabled) return;

            const token = await getAPIToken().catch(console.error);

            if (token) {
                console.info('[TokenService] Parent responded with token, saving to localStorage');
                TokenService.saveToken(token);
            }
            else console.info('[TokenService] Parent context did not respond with anything');

        } else console.info('[TokenService] Token already preset')
    },
    /** @returns The stored access token, or `null` when not logged in */
    getToken(): string | null {
        return localStorage.getItem(TOKEN_KEY)
    },
    /**
     * @param provider SSO provider key, e.g. 'google' — determines the localStorage key
     */
    getSSONonce(provider: string): SSONonce {
      const token = localStorage.getItem(`sso_${provider}_nonce`);
      if (!token) return { nonce: null, extraData: [] };
      const [nonce, ...extraData] = token.split('::');
      // split() always yields at least one element
      return { nonce: nonce!, extraData };
    },
    /**
     * Reads the CSRF token from the cookie, creating (and setting) a fresh
     * random one when no cookie exists yet.
     */
    getCSRFToken(): string {
        const match = document.cookie.match(new RegExp('(^| )csrf_token=([^;]+)'));
        if (match) return match[2]!;

        // Create one yourself
        const csrfToken = crypto.randomUUID();
        const domain = window.location.host.startsWith("localhost:8080")
          ? undefined
          : '.' + window.location.host.split(':').slice(-2).join('.');

        if (domain) {
          document.cookie = `csrf_token=${csrfToken}; path=/; domain=${domain}; SameSite=Lax; max-age=60`;
        } else {
            document.cookie = `csrf_token=${csrfToken}; path=/; SameSite=Lax;`;
        }

        console.log("Created custom token");
        return csrfToken;
    },

    saveToken(accessToken: string): void {
        localStorage.setItem(TOKEN_KEY, accessToken)
    },
    /**
     * @param provider SSO provider key — determines the localStorage key
     * @param nonce Random nonce to store
     * @param extraData Extra values appended to the nonce with `::` separators
     */
    saveSSONonce(provider: string, nonce: string, extraData: string[] = []): void {
        if (extraData) nonce += `::${extraData.join('::')}`;
        localStorage.setItem(`sso_${provider}_nonce`, nonce);
    },

    removeToken(): void {
        localStorage.removeItem(TOKEN_KEY)
    },
    removeSSONonce(provider: string): void {
        localStorage.removeItem(`sso_${provider}_nonce`);
    }
}

export { TokenService }
